import React from 'react';
import { useTranslation } from 'react-i18next';
import { toPlainText } from 'modules/editor';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ScatterChart,
  Scatter,
  ReferenceLine,
} from 'recharts';
import type { ItemStat, ScoreBucket } from 'modules/analytics';
import { axisTick, gridStroke, ChartTooltip } from './shared';

/** Ink: the bar under the pointer. */
const INK = 'hsl(var(--chart-1))';
/** Quiet grey: every other bar. */
const QUIET = 'hsl(var(--chart-2))';

/** Bars stay flat: the hovered one turns ink, no column wash behind it. */
const barCursor = false;

/** Score distribution histogram — flat grey columns, the hovered one in ink. */
export const ScoreDistributionBar: React.FC<{ data: ScoreBucket[]; height?: number }> = ({ data, height = 256 }) => {
  const { t } = useTranslation();
  return (
    <div style={{ height }} className="-mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 12, right: 8, left: -14, bottom: 0 }}>
          <CartesianGrid stroke={gridStroke} strokeOpacity={0.5} vertical={false} />
          <XAxis dataKey="range" axisLine={false} tickLine={false} tick={axisTick} dy={8} />
          <YAxis axisLine={false} tickLine={false} tick={axisTick} width={30} allowDecimals={false} />
          <Tooltip cursor={barCursor} content={<ChartTooltip />} />
          <Bar
            dataKey="count"
            name={t('analytics_candidates')}
            fill={QUIET}
            activeBar={{ fill: INK }}
            radius={[2, 2, 0, 0]}
            maxBarSize={52}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const truncate = (s: string, n = 28) => {
  const plain = toPlainText(s);
  return plain.length > n ? `${plain.slice(0, n)}…` : plain;
};
// Status tokens, so item difficulty reads in the same red/amber/green as every other state.
const HARD = 'hsl(var(--destructive))';
const EASY = 'hsl(var(--warning))';
const OK = 'hsl(var(--success))';

/** Per-question difficulty (p-value as %). Lowest = hardest, listed first. */
export const ItemDifficultyBar: React.FC<{ items: ItemStat[]; height?: number }> = ({ items, height = 360 }) => {
  const { t } = useTranslation();
  const data = items.slice(0, 20).map((it, i) => ({
    label: `Q${it.order || i + 1} · ${truncate(it.prompt)}`,
    difficulty: Math.round(it.pValue * 100),
    flag: it.flag,
  }));
  return (
    <div style={{ height }} className="-mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
          <CartesianGrid stroke={gridStroke} strokeOpacity={0.5} horizontal={false} />
          <XAxis type="number" domain={[0, 100]} axisLine={false} tickLine={false} tick={axisTick} unit="%" />
          <YAxis
            type="category"
            dataKey="label"
            width={180}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
          />
          <Tooltip cursor={barCursor} content={<ChartTooltip valueFormatter={(v) => `${v}%`} />} />
          <Bar dataKey="difficulty" name={t('analytics_items_difficulty')} radius={[0, 2, 2, 0]} maxBarSize={26}>
            {data.map((d) => (
              <Cell key={d.label} fill={d.flag === 'too_hard' ? HARD : d.flag === 'too_easy' ? EASY : OK} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Difficulty (x) vs discrimination (y) scatter — quality map of items. */
export const DiscriminationScatter: React.FC<{ items: ItemStat[]; height?: number }> = ({ items, height = 320 }) => {
  const { t } = useTranslation();
  const data = items
    .filter((it) => it.discrimination.pointBiserial != null)
    .map((it) => ({
      x: Math.round(it.pValue * 100),
      y: it.discrimination.pointBiserial as number,
      flagged: it.flag != null,
      label: truncate(it.prompt, 36),
    }));
  return (
    <div style={{ height }} className="-mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 10, right: 16, left: -8, bottom: 10 }}>
          <CartesianGrid stroke={gridStroke} strokeOpacity={0.5} />
          <XAxis
            type="number"
            dataKey="x"
            name={t('analytics_items_difficulty')}
            domain={[0, 100]}
            axisLine={false}
            tickLine={false}
            tick={axisTick}
            unit="%"
          />
          <YAxis
            type="number"
            dataKey="y"
            name={t('analytics_items_discrimination')}
            domain={[-1, 1]}
            axisLine={false}
            tickLine={false}
            tick={axisTick}
          />
          <ReferenceLine y={0.2} stroke={OK} strokeOpacity={0.6} strokeDasharray="4 4" />
          <Tooltip
            cursor={{ strokeDasharray: '4 4', stroke: INK, opacity: 0.45 }}
            content={
              <ChartTooltip
                resolveLabel={(p) => (p[0]?.payload?.label as string) ?? ''}
                valueFormatter={(v, _n, e) =>
                  e.dataKey === 'y' ? v.toFixed(2) : `${v}%`
                }
              />
            }
          />
          <Scatter data={data} name={t('analytics_tab_items')}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.flagged ? HARD : INK} fillOpacity={0.85} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Integrity events grouped by type — flat grey columns, the hovered one in ink. */
export const IntegrityEventBar: React.FC<{ data: Array<{ type: string; count: number }>; height?: number }> = ({
  data,
  height = 256,
}) => {
  const { t } = useTranslation();
  const mapped = data.map((d) => ({ label: t(`analytics_event_${d.type.toLowerCase()}`), count: d.count }));
  return (
    <div style={{ height }} className="-mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={mapped} margin={{ top: 12, right: 8, left: -14, bottom: 0 }}>
          <CartesianGrid stroke={gridStroke} strokeOpacity={0.5} vertical={false} />
          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={axisTick} dy={8} interval={0} />
          <YAxis axisLine={false} tickLine={false} tick={axisTick} width={30} allowDecimals={false} />
          <Tooltip cursor={barCursor} content={<ChartTooltip />} />
          <Bar
            dataKey="count"
            name={t('analytics_events')}
            fill={QUIET}
            activeBar={{ fill: INK }}
            radius={[2, 2, 0, 0]}
            maxBarSize={52}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
